/**
 * Single source of truth for the GLOBAL HEADER SHELL GEOMETRY — the outer bar
 * layout that must be identical across every top-of-page header so switching
 * between the marketing Homepage (`/`, landing-v2/NavbarV2) and the authenticated
 * "My Projects" dashboard (`/dashboard`, layout/Header) is a seamless transition
 * (no logo jump, no height change, no padding shift). See Workplace post
 * 1573952564362041 (follow-up to the brand-chrome unification in post
 * 1562612608829370 / PR #2255, which shared BrandMark + UserMenu but left the
 * shell geometry hand-duplicated and divergent).
 *
 * The brand glyph + user menu are already shared (BrandMark, UserMenu). These
 * constants finish the job by sharing the SHELL: horizontal padding, height,
 * inner max-width, item alignment, and the right-cluster gap.
 *
 * ── Why plain string constants (not a <HeaderShell> component) ──────────────
 * The consumers have legitimately different outer elements — landing uses an
 * animated <motion.nav> (transparent-over-hero → fixed/black on scroll) and the
 * dashboard uses a static sticky solid <header> — so a shared shell component
 * would have to abstract over both; sharing just the geometry strings is smaller
 * and keeps each surface's chrome intact.
 *
 * Consumers: NavbarV2 (Homepage) and layout/Header (dashboard) import these
 * directly. The pre-consent cookie backdrop (consent/ConsentBackdrop.tsx) must
 * stay within the React + static-asset boundary (RFC-0782 / RQ10773473) and so
 * does NOT import this module — it hand-mirrors the same values, and a vitest
 * guard (sharedHeader.test.tsx) asserts its inlined geometry still equals these
 * constants so the mirror cannot silently drift.
 *
 * ⚠️ These strings equal the landing's current geometry verbatim, so the landing
 * (and its ConsentBackdrop mirror) render byte-identically. The dashboard header
 * adopts these values — that is the visible fix.
 */

/** Outer horizontal padding wrapper (responsive: 16px → 24px → 84px). */
export const HEADER_PADDING_X = 'px-4 sm:px-6 md:px-[84px]'

/**
 * The centered row inside the padding wrapper — a FLOATING PILL: capped at the
 * brand max-width, 48px tall (56px on md), rounded, translucent and blurred,
 * inset from the top.
 *
 * ONE constant on purpose. There used to be a second (HEADER_PILL_ROW) so the
 * landing could float while the dashboard docked; that immediately produced the
 * height/shape jump this module exists to prevent. Both surfaces now render the
 * same pill, so there is no second row for one of them to drift onto.
 *
 * Surfaces differ only in the OUTER shell: the landing is absolute and scrolls
 * away, the dashboard is sticky and stays. Both shells are transparent — the
 * pill supplies its own background, so it reads as floating over content on
 * either page.
 */
export const HEADER_ROW =
  'mx-auto mt-3 flex h-12 w-full max-w-[1273px] items-center justify-between rounded-full border border-white/15 bg-black/30 pl-4 pr-1 backdrop-blur-xl md:mt-4 md:h-14 md:pl-6 md:pr-1'

/** Gap between the right-cluster action items (nav links, pill, user menu). */
export const HEADER_ACTIONS_GAP = 'gap-3'
